import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Upload, 
  FileText, 
  AlertTriangle, 
  Loader2, 
  Info, 
  FileSignature,
  X,
  Mail,
  ExternalLink,
  Copy
} from "lucide-react";
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { uploadFile } from '@/lib/fileHandler';
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { v4 as uuidv4 } from 'uuid';

interface ChatInsuranceOfferFormProps {
  onEmailGenerated: (content: string) => void;
  onClose: () => void;
  onLinkGenerated?: (linkData: {url: string, id: string, type: string, timestamp: string}) => void;
}

const ChatInsuranceOfferForm = ({ onEmailGenerated, onClose, onLinkGenerated }: ChatInsuranceOfferFormProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [comments, setComments] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [includeKnowledgeBase, setIncludeKnowledgeBase] = useState(false);
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [apiError, setApiError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const { user, credits, refreshProfile } = useAuth();
  const { toast } = useToast();

  const steps = [
    { id: 1, title: 'Dokument hochladen' },
    { id: 2, title: 'Optionen konfigurieren' },
    { id: 3, title: 'Übersicht & Bestätigung' }
  ];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      
      if (selectedFile.type === 'application/pdf') {
        setFile(selectedFile);
        setApiError(null);
      } else {
        toast({
          title: "Ungültiges Dateiformat",
          description: "Bitte laden Sie nur PDF-Dateien hoch.",
          variant: "destructive"
        });
      }
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      toast({
        title: "Nicht angemeldet",
        description: "Bitte melden Sie sich an, um diese Funktion zu nutzen.",
        variant: "destructive"
      });
      return;
    }

    if (!file) {
      toast({
        title: "Keine Datei",
        description: "Bitte laden Sie eine Versicherungsofferte hoch.",
        variant: "destructive"
      });
      return;
    }

    if (credits < 1) {
      toast({
        title: "Nicht genügend Credits",
        description: `Für die Analyse benötigen Sie 1 Credit. Sie haben derzeit ${credits} Credits.`,
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);
    setRetryCount(0);
    setApiError(null);
    setGeneratedContent(null);
    setSharingId(null);

    try {
      setUploadProgress(10);
      const uploadedFile = await uploadFile(file, user.id);
      
      if (!uploadedFile) {
        throw new Error(`Fehler beim Hochladen der Datei: ${file.name}`);
      }
      
      setUploadProgress(30);
      
      try {
        // Step 1: Analyze the document
        const { data: analysisData, error: analysisError } = await supabase.functions.invoke('document-analysis', {
          body: {
            fileId: uploadedFile.id,
            analysisType: 'insurance_offer',
            language: 'de'
          }
        });
        
        if (analysisError || !analysisData?.analysis) {
          throw new Error(`Fehler bei der Analyse der Datei: ${file.name}`);
        }
        
        console.log('Document analysis completed:', analysisData);
        setUploadProgress(50);
        
        // Create a unique sharing ID - using a substring of uuid to keep it shorter
        const newSharingId = uuidv4().substring(0, 8);
        console.log('Generated new sharing ID:', newSharingId);
        
        // Create an entry in the insurance_offers table before generating the page
        const { data: offerData, error: offerError } = await supabase
          .from('insurance_offers')
          .insert({
            user_id: user.id,
            file_id: uploadedFile.id,
            requires_signature: requiresSignature,
            title: uploadedFile.filename,
            status: 'processing',
            sharing_id: newSharingId,
            analysis_result: analysisData.analysis
          })
          .select('id, sharing_id')
          .single();
        
        if (offerError || !offerData) {
          console.error('Error creating offer entry:', offerError);
          throw new Error('Fehler beim Erstellen des Angebots in der Datenbank');
        }
        
        console.log('Created offer entry:', offerData);
        setUploadProgress(60);
        
        // Step 2: Generate the page content
        const { data: pageData, error: pageError } = await supabase.functions.invoke('page-generation', {
          body: {
            pageType: 'offer',
            offerId: offerData.id,
            companyInfo: localStorage.getItem('companyInfo') ? JSON.parse(localStorage.getItem('companyInfo')!) : null,
            includeSignature: requiresSignature,
            language: 'de'
          }
        });
        
        if (pageError || !pageData) {
          console.error('Error generating page:', pageError);
          
          // Update the offer status to error
          await supabase
            .from('insurance_offers')
            .update({ status: 'error' })
            .eq('id', offerData.id);
            
          throw new Error(pageError?.message || 'Fehler bei der Generierung der Angebotsseite');
        }
        
        console.log('Offer page generated:', pageData);
        setUploadProgress(100);
        
        // Create the offer URL with the new URL pattern
        const offerUrl = `${window.location.origin}/offer/${newSharingId}`;
        console.log('Created offer URL:', offerUrl);
        
        // Don't automatically generate an email content, just set the sharing link
        setSharingId(newSharingId);
        
        // Pass the link data to parent component if callback exists
        if (onLinkGenerated) {
          onLinkGenerated({
            url: offerUrl,
            id: newSharingId,
            type: 'offer',
            timestamp: new Date().toISOString()
          });
        }
        
        // Show success message
        toast({
          title: "Angebot erstellt",
          description: "Das Versicherungsangebot wurde erfolgreich erstellt.",
        });
        
        refreshProfile();
      } catch (error: any) {
        setApiError(error.message || "Es gab ein Problem bei der API-Anfrage. Bitte versuchen Sie es später erneut.");
        console.error('Processing error:', error);
        throw error;
      }
      
    } catch (error: any) {
      console.error('Error in offer creation:', error);
      toast({
        title: "Fehler bei der Erstellung",
        description: error.message || "Es gab einen Fehler bei der Verarbeitung. Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateEmailTemplate = () => {
    if (sharingId) {
      const offerUrl = `${window.location.origin}/offer/${sharingId}`;
      
      // Dispatch custom event to start a new conversation with a template suggestion
      window.dispatchEvent(new CustomEvent('suggest-email-template', {
        detail: {
          offerUrl,
          offerTitle: file?.name || 'Versicherungsangebot'
        }
      }));
      
      // Close the form
      onClose();
    }
  };

  if (sharingId) {
    const offerUrl = `${window.location.origin}/offer/${sharingId}`;
    
    return (
      <div className="space-y-4 mt-2">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-white">Versicherungsangebot</h3>
          <Button 
            variant="ghost" 
            size="sm"
            className="text-gray-400 hover:text-white p-1 h-auto"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="p-3 bg-green-900/20 rounded-lg border border-green-800/40 text-white">
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-green-400 flex-shrink-0" />
            <div>Angebot wurde erfolgreich erstellt!</div>
          </div>
        </div>
        
        <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-sm text-gray-300">Öffentlicher Link zum Angebot:</span>
          </div>
          <div className="text-xs text-gray-400 bg-gray-800 p-2 rounded border border-gray-700 break-all mb-2">
            {offerUrl}
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="h-7 text-xs border-gray-700 bg-gray-800 flex-1"
              onClick={() => {
                navigator.clipboard.writeText(offerUrl);
                toast({
                  title: "Link kopiert",
                  description: "Der Link wurde in die Zwischenablage kopiert.",
                });
              }}
            >
              <Copy className="h-3 w-3 mr-1" />
              Link kopieren
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-gray-700 bg-gray-800 flex-1"
              onClick={() => window.open(offerUrl, '_blank')}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Öffnen
            </Button>
          </div>
        </div>
        
        <p className="text-sm text-gray-400">
          Möchten Sie eine E-Mail erstellen, um dieses Angebot zu versenden?
        </p>
        
        <div className="flex gap-2">
          <Button
            type="button"
            className="w-full bg-jade hover:bg-jade-light text-white"
            onClick={handleCreateEmailTemplate}
          >
            <Mail className="h-4 w-4 mr-2" />
            E-Mail-Vorlage erstellen
          </Button>
        </div>
        
        <Button
          type="button"
          variant="ghost"
          className="w-full border border-gray-700 hover:bg-gray-800"
          onClick={onClose}
        >
          Zurück zum Chat
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-2">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-white">Versicherungsangebot</h3>
        <Button 
          variant="ghost" 
          size="sm"
          className="text-gray-400 hover:text-white p-1 h-auto"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Progress steps */}
      <div className="flex justify-between mb-4">
        {steps.map((step) => (
          <div 
            key={step.id} 
            className={`flex flex-col items-center ${currentStep === step.id ? 'text-jade' : 'text-gray-500'}`}
          >
            <div 
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mb-1
                ${currentStep === step.id ? 'bg-jade text-white' : 
                currentStep > step.id ? 'bg-jade/30 text-white' : 'bg-gray-800 text-gray-400'}`}
            >
              {step.id}
            </div>
            <span className="text-xs hidden sm:block">{step.title}</span>
          </div>
        ))}
      </div>

      {apiError && (
        <div className="p-3 bg-red-900/20 rounded-lg border border-red-800/40 text-white">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div>{apiError}</div>
          </div>
        </div>
      )}

      {!generatedContent && currentStep === 1 && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="insurance-file-upload-chat" className="text-white flex items-center gap-1.5">
              Versicherungsofferte (PDF)
            </Label>
            
            <div 
              className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-jade transition-colors cursor-pointer"
              onClick={() => document.getElementById('insurance-file-upload-chat')?.click()}
            >
              <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-300 mb-1">Datei hierher ziehen oder klicken zum Hochladen</p>
              <p className="text-xs text-gray-500">Nur PDF-Dateien</p>
              <Input
                id="insurance-file-upload-chat"
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
            
            {file && (
              <div className="mt-2 p-3 bg-gray-800/60 rounded flex items-center justify-between">
                <div className="flex items-center space-x-2 w-5/6">
                  <FileText className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  <span className="text-sm text-gray-200 truncate">{file.name}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setFile(null)}
                  className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
                >
                  &times;
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {!generatedContent && currentStep === 2 && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="comments" className="text-white">
              Beraterhinweise (optional)
            </Label>
            <Textarea
              id="comments"
              placeholder="Fügen Sie spezifische Anweisungen hinzu..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="h-24 bg-gray-800 border-gray-700 text-white resize-none"
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-800/40 rounded-lg">
            <div className="flex items-center gap-2">
              <Info className="h-5 w-5 text-jade" />
              <div>
                <p className="text-sm font-medium text-white">Wissensdatenbank einbeziehen</p>
                <p className="text-xs text-gray-400">Relevante Informationen aus der Wissensdatenbank werden berücksichtigt</p>
              </div>
            </div>
            <Switch
              checked={includeKnowledgeBase}
              onCheckedChange={setIncludeKnowledgeBase}
              className="data-[state=checked]:bg-jade"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-800/40 rounded-lg">
            <div className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-jade" />
              <div>
                <p className="text-sm font-medium text-white">Unterschrift anfordern</p>
                <p className="text-xs text-gray-400">Das Angebot wird mit einer Unterschriftsfunktion versehen</p>
              </div>
            </div>
            <Switch
              checked={requiresSignature}
              onCheckedChange={setRequiresSignature}
              className="data-[state=checked]:bg-jade"
            />
          </div>
        </div>
      )}

      {!generatedContent && currentStep === 3 && (
        <div className="space-y-4">
          <div className="p-4 bg-gray-800/40 rounded-lg border border-gray-700">
            <h3 className="font-medium text-white mb-2">Zusammenfassung</h3>
            <ul className="text-sm text-gray-300 space-y-2">
              <li className="flex justify-between">
                <span>Datei:</span>
                <span className="font-medium text-white">{file?.name}</span>
              </li>
              <li className="flex justify-between">
                <span>Unterschrift anfordern:</span>
                <span className="font-medium text-white">{requiresSignature ? 'Ja' : 'Nein'}</span>
              </li>
              <li className="flex justify-between">
                <span>Wissensdatenbank:</span>
                <span className="font-medium text-white">{includeKnowledgeBase ? 'Einbezogen' : 'Nicht einbezogen'}</span>
              </li>
              <li className="flex justify-between">
                <span>Benötigte Credits:</span>
                <span className="font-medium text-jade">1 Credit</span>
              </li>
            </ul>
          </div>

          <div className="p-3 bg-amber-900/20 border border-amber-800/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-300 flex-shrink-0" />
              <p className="text-sm text-amber-200">
                Die Analyse kann je nach Größe des Dokuments bis zu einer Minute dauern.
              </p>
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          <div className="h-2 w-full bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-jade rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-400 text-center">
            {uploadProgress < 40 ? "Hochladen..." : 
             uploadProgress < 60 ? "Analysieren..." : 
             uploadProgress < 80 ? "Angebot wird erstellt..." : "E-Mail wird generiert..."}
          </p>
        </div>
      )}

      {!generatedContent && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-400 flex items-center gap-1.5">
            <Badge variant="outline" className="bg-jade/20 text-jade border-jade/30">
              Credits: {credits}
            </Badge>
          </div>
          
          <div className="flex gap-2">
            {currentStep > 1 && !isLoading && (
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                className="border-gray-700 text-gray-200 hover:bg-gray-800"
                disabled={isLoading}
              >
                Zurück
              </Button>
            )}
            
            {currentStep < steps.length && !isLoading && (
              <Button
                type="button"
                onClick={nextStep}
                className="bg-jade hover:bg-jade-light text-white"
                disabled={isLoading || (currentStep === 1 && !file)}
              >
                Weiter
              </Button>
            )}
            
            {currentStep === steps.length && !isLoading && (
              <Button
                type="button"
                onClick={handleSubmit}
                className="bg-jade hover:bg-jade-light text-white"
                disabled={isLoading || !file}
              >
                Angebot erstellen
              </Button>
            )}
            
            {isLoading && (
              <Button
                type="button"
                className="bg-jade hover:bg-jade-light text-white"
                disabled={true}
              >
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Verarbeitung...
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInsuranceOfferForm;
